import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Status, Typography } from '@kc-console/common-ui';
import { getCustomFormatDate } from '@kc-console/core';

import type { EventCommentItem } from '@/pages/events/model/types';

import { getEventStatusVariant } from '@/pages/events/model/getEventStatusVariant';

const DATE_FORMAT = 'yyyy.MM.dd (EEE) HH:mm';

interface DescriptionSectionContentProps {
  comments: EventCommentItem[];
}

/**
 * 이벤트 상세 - 설명 섹션.
 * @description
 * - 코멘트 목록을 상태별 아이콘 + 일시 헤더와 줄바꿈을 보존한 설명 본문으로 타임라인 형태로 노출한다.
 * @example
 * <DescriptionSectionContent comments={comments} />
 */
const DescriptionSectionContent = ({ comments }: DescriptionSectionContentProps) => {
  if (comments.length === 0) {
    return (
      <EmptyRoot>
        <Typography variant="body/normal/md" color="text/weak/default">
          등록된 설명이 없습니다.
        </Typography>
      </EmptyRoot>
    );
  }

  return (
    <TimelineRoot>
      {comments.map((item, index) => {
        const isLast = index === comments.length - 1;
        const variant = getEventStatusVariant(item.status);

        return (
          <TimelineItem key={`${item.date}-${index}`}>
            <Header>
              <IndicatorSlot>
                <Status status={variant} hideLabel size="lg" />
              </IndicatorSlot>
              <Typography variant="label/medium/sm" color="text/weaker/default">
                {getCustomFormatDate(item.date, DATE_FORMAT)}
              </Typography>
            </Header>
            <Body>
              <ConnectorSlot hasConnector={!isLast}>{!isLast && <Connector />}</ConnectorSlot>
              <DescriptionText variant="body/normal/md" color="text/default">
                {item.comment}
              </DescriptionText>
            </Body>
          </TimelineItem>
        );
      })}
    </TimelineRoot>
  );
};

export default DescriptionSectionContent;

const TimelineRoot = styled('ol')`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TimelineItem = styled('li')`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Header = styled('div')`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const Body = styled('div')`
  display: flex;
  align-items: stretch;
  gap: 6px;
  width: 100%;
  padding: 2px 0;
`;

const IndicatorSlot = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  min-width: 24px;
  height: 24px;
  padding: 8px 10px;
  box-sizing: border-box;
`;

const ConnectorSlot = styled('div', {
  shouldForwardProp: prop => prop !== 'hasConnector',
})<{ hasConnector: boolean }>(
  ({ hasConnector }) => css`
    display: flex;
    justify-content: center;
    width: 24px;
    min-width: 24px;
    padding-bottom: ${hasConnector ? '0' : '4px'};
  `,
);

const Connector = styled('span')(
  ({ theme }) => css`
    width: 1px;
    background-color: ${theme.color['border/weak/default']};
  `,
);

const DescriptionText = styled(Typography)`
  flex: 1 1 auto;
  min-width: 0;
  padding: 2px 0 4px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const EmptyRoot = styled('div')(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background-color: ${theme.color['surface/neutral/weaker/default']};
    border-radius: 2px;
  `,
);
